// AdminArchetypePlaybooksPage.js — LLM-drafted archetype playbooks per domain.
// Master-admin only. One reusable playbook per (domain, archetype) that any
// consultant can reference before making a call of that archetype.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import {
  BookOpen, Loader2, RefreshCw, Sparkles, AlertCircle, Copy, CheckCircle2,
  ChevronDown, ChevronUp, Zap, Pencil,
} from 'lucide-react';
import ArchetypePlaybookEditor from './ArchetypePlaybookEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARCHETYPE_LABELS = {
  CALLBACK: 'Callback (Warm Follow-up)',
  NEW_ENQUIRY: 'New Enquiry (First Contact)',
  TOUR_FOLLOWUP: 'Tour Follow-up (Post-Visit Close)',
  MEMBER_ADMIN: 'Member Admin (Billing / Holds / Retention)',
  PRICE_QUERY: 'Price Query (Cost Question)',
  DORMANT_REVIVAL: 'Dormant Revival (>60d Cold Lead)',
  NEW_SALES_ENQUIRY: 'New Sales Enquiry (Systems / AI / Upgrades)',
  SUPPORT_FAULT: 'Support / Fault (Help Desk / Install)',
  IT_TECHNICAL: 'IT Technical (Config / Porting)',
  ACCOUNT_ADMIN: 'Account Admin (Invoices / Licenses)',
  CALLBACK_FOLLOWUP: 'Callback Follow-up (Sales or Service)',
  RETENTION_CHANGE: 'Retention / Change (Downgrades / Renewals)',
};

const ARCHETYPE_COLORS = {
  CALLBACK: 'from-teal-500/20 to-cyan-500/10 border-teal-500/30',
  NEW_ENQUIRY: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30',
  TOUR_FOLLOWUP: 'from-violet-500/20 to-purple-500/10 border-violet-500/30',
  MEMBER_ADMIN: 'from-sky-500/20 to-blue-500/10 border-sky-500/30',
  PRICE_QUERY: 'from-amber-500/20 to-orange-500/10 border-amber-500/30',
  DORMANT_REVIVAL: 'from-rose-500/20 to-pink-500/10 border-rose-500/30',
  NEW_SALES_ENQUIRY: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30',
  SUPPORT_FAULT: 'from-rose-500/20 to-pink-500/10 border-rose-500/30',
  IT_TECHNICAL: 'from-sky-500/20 to-blue-500/10 border-sky-500/30',
  ACCOUNT_ADMIN: 'from-amber-500/20 to-orange-500/10 border-amber-500/30',
  CALLBACK_FOLLOWUP: 'from-teal-500/20 to-cyan-500/10 border-teal-500/30',
  RETENTION_CHANGE: 'from-violet-500/20 to-purple-500/10 border-violet-500/30',
};

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const _copy = (text, setStatus) => {
  navigator.clipboard.writeText(text).then(() => {
    setStatus('copied');
    setTimeout(() => setStatus(''), 1500);
  });
};

const PlaybookCard = ({ playbook, onDraft, drafting, headers, domainId, onRefreshList }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const { archetype, sections, version, generated_at, generated_by, llm_tier, edited_at, edited_by } = playbook;
  const drafted = !!sections;
  const colorClasses = ARCHETYPE_COLORS[archetype] || 'from-white/5 to-white/0 border-white/10';

  const fullText = useMemo(() => {
    if (!sections) return '';
    const lines = [
      `# ${ARCHETYPE_LABELS[archetype]} — Playbook v${version}`,
      '',
      `## Opening line`,
      sections.opening_line,
      '',
      '## Checklist',
      ...(sections.checklist || []).map((s, i) => `${i + 1}. ${s}`),
      '',
      '## Discovery questions (Green Brain)',
      ...(sections.discovery_questions || []).map((s) => `- ${s}`),
      '',
      '## Objection handling',
      ...(sections.objection_handling || []).flatMap((o) => [
        `**${o.objection}**`,
        `→ ${o.response}`,
        '',
      ]),
      '## Close / defined next step',
      sections.close_or_next_step,
      '',
      '## DISC tone notes',
      ...Object.entries(sections.disc_notes || {}).map(([k, v]) => `- ${k}: ${v}`),
      '',
      '## Do NOT say',
      ...(sections.do_not_say || []).map((s) => `- ${s}`),
    ];
    return lines.join('\n');
  }, [sections, archetype, version]);

  return (
    <div
      className={`bg-gradient-to-br ${colorClasses} border rounded-2xl p-5`}
      data-testid={`archetype-card-${archetype}`}
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold text-base flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-teal-300 shrink-0" />
            {ARCHETYPE_LABELS[archetype]}
          </h3>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
            {drafted ? (
              <>
                <span className="bg-teal-500/20 text-teal-200 px-2 py-0.5 rounded-full font-mono">v{version}</span>
                <span className="text-white/50">Drafted {_fmtDate(generated_at)}</span>
                {llm_tier && <span className="text-white/40">· {llm_tier}</span>}
                {edited_at && <span className="text-amber-300">· edited {_fmtDate(edited_at)}</span>}
              </>
            ) : (
              <span className="bg-white/10 text-white/60 px-2 py-0.5 rounded-full">Not yet drafted</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {drafted && (
            <>
              <button
                onClick={() => { setEditing((v) => !v); setExpanded(true); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 rounded-lg"
                data-testid={`archetype-edit-${archetype}`}
                title={editing ? 'Close editor' : 'Edit inline'}
              >
                <Pencil className="w-3.5 h-3.5" />
                {editing ? 'Close editor' : 'Edit'}
              </button>
              <button
                onClick={() => _copy(fullText, setCopyStatus)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 rounded-lg"
                data-testid={`archetype-copy-${archetype}`}
                title="Copy full playbook as markdown"
              >
                {copyStatus === 'copied' ? <CheckCircle2 className="w-3.5 h-3.5 text-teal-300" /> : <Copy className="w-3.5 h-3.5" />}
                {copyStatus === 'copied' ? 'Copied' : 'Copy'}
              </button>
            </>
          )}
          <button
            onClick={() => onDraft(archetype)}
            disabled={drafting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold rounded-lg"
            data-testid={`archetype-draft-${archetype}`}
          >
            {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {drafted ? 'Redraft' : 'Draft with AI'}
          </button>
        </div>
      </div>

      {drafted && (
        <>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-teal-300 hover:text-teal-200 flex items-center gap-1"
            data-testid={`archetype-toggle-${archetype}`}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? 'Hide playbook' : 'Show playbook'}
          </button>

          {expanded && (
            <div className="mt-4 space-y-4 text-sm" data-testid={`archetype-body-${archetype}`}>
              {editing ? (
                <ArchetypePlaybookEditor
                  playbook={playbook}
                  domainId={domainId}
                  headers={headers}
                  onSaved={() => { setEditing(false); onRefreshList?.(); }}
                  onCancel={() => setEditing(false)}
                />
              ) : (
              <>
              <Section title="Opening line">
                <blockquote className="border-l-2 border-teal-400 pl-3 text-white/90 italic">
                  &ldquo;{sections.opening_line}&rdquo;
                </blockquote>
              </Section>

              <Section title="Checklist">
                <ol className="list-decimal pl-5 space-y-1 text-white/80">
                  {(sections.checklist || []).map((step, i) => (<li key={i}>{step}</li>))}
                </ol>
              </Section>

              <Section title="Discovery questions (Green Brain)">
                <ul className="list-disc pl-5 space-y-1 text-white/80">
                  {(sections.discovery_questions || []).map((q, i) => (<li key={i}>{q}</li>))}
                </ul>
              </Section>

              <Section title="Objection handling">
                <div className="space-y-2">
                  {(sections.objection_handling || []).map((o, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3">
                      <div className="text-amber-300 text-xs font-mono">{o.objection}</div>
                      <div className="text-white/80 text-sm mt-1">→ {o.response}</div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Close / defined next step">
                <p className="text-white/80">{sections.close_or_next_step}</p>
              </Section>

              <Section title="DISC tone notes">
                <div className="grid md:grid-cols-2 gap-2">
                  {Object.entries(sections.disc_notes || {}).map(([disc, note]) => (
                    <div key={disc} className="bg-white/5 border border-white/10 rounded-lg p-2.5">
                      <div className="text-teal-300 font-mono text-xs">{disc}</div>
                      <div className="text-white/75 text-xs mt-1">{note}</div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Do NOT say">
                <ul className="list-disc pl-5 space-y-1 text-rose-200/90">
                  {(sections.do_not_say || []).map((s, i) => (<li key={i}>{s}</li>))}
                </ul>
              </Section>

              {generated_by && (
                <div className="text-[10px] text-white/40 pt-2 border-t border-white/10">
                  Drafted by {generated_by}{edited_by ? ` · last edited by ${edited_by}` : ''}
                </div>
              )}
              </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h4 className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-1.5">{title}</h4>
    {children}
  </div>
);

const AdminArchetypePlaybooksPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draftingArchetype, setDraftingArchetype] = useState(null);
  const [draftingAll, setDraftingAll] = useState(false);
  const [draftAllResult, setDraftAllResult] = useState(null);

  // Load list of domains
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/domains`, { headers });
        const rows = Array.isArray(r.data) ? r.data : (r.data.domains || []);
        setDomains(rows);
        // Auto-pick TotalFusion if present, else first domain
        const totalfusion = rows.find((d) => (d.name || '').toLowerCase().includes('total fusion') || (d.yabbit_domain || '').toLowerCase().includes('totalfusion'));
        const pick = totalfusion || rows[0];
        if (pick) setDomainId(pick.id);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load domains.');
      }
    })();
  }, [headers]);

  const load = useCallback(async () => {
    if (!domainId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/tfvf/v1_2/archetype-playbooks?domain_id=${domainId}`, { headers });
      setPlaybooks(r.data?.playbooks || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load playbooks.');
    } finally {
      setLoading(false);
    }
  }, [headers, domainId]);

  useEffect(() => { if (domainId) load(); }, [domainId, load]);

  const draftOne = useCallback(async (archetype) => {
    setDraftingArchetype(archetype); setError('');
    try {
      await axios.post(
        `${API}/admin/tfvf/v1_2/archetype-playbooks/draft`,
        { domain_id: domainId, archetype },
        { headers },
      );
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || `Failed to draft ${archetype}.`);
    } finally {
      setDraftingArchetype(null);
    }
  }, [domainId, headers, load]);

  const draftAll = useCallback(async () => {
    if (!window.confirm('Draft all 6 archetype playbooks? This will call the LLM 6 times and overwrite existing drafts (prior versions are kept in history).')) return;
    setDraftingAll(true); setError(''); setDraftAllResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/tfvf/v1_2/archetype-playbooks/draft-all`,
        { domain_id: domainId },
        { headers, timeout: 240000 },
      );
      setDraftAllResult(r.data?.results || {});
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Draft-all failed.');
    } finally {
      setDraftingAll(false);
    }
  }, [domainId, headers, load]);

  const draftedCount = playbooks.filter((p) => p.sections).length;

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="archetype-playbooks-page">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-teal-400" />
              Archetype Playbooks
              <span className="ml-2 text-xs bg-teal-500/15 text-teal-300 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">TFVF V1.2</span>
            </h1>
            <p className="text-sm text-white/60 mt-1 max-w-3xl">
              LLM-drafted playbooks — one per CRM archetype. Every consultant can reference these before or during a call. Drafts are Green-Brain + DISC-aware, and pull from the tenant&apos;s approved-phrase library where available.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading || !domainId}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50"
            data-testid="archetype-reload"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reload
          </button>
        </div>

        {/* Domain picker + Draft-all */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-6 flex items-center flex-wrap gap-3">
          <label className="text-white/60 text-xs uppercase tracking-wider font-semibold shrink-0">Domain</label>
          <select
            value={domainId}
            onChange={(e) => setDomainId(e.target.value)}
            className="bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 flex-1 min-w-[240px]"
            data-testid="archetype-domain-picker"
          >
            <option value="">— select a domain —</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>{d.name || d.yabbit_domain} ({d.id.slice(0, 8)}…)</option>
            ))}
          </select>
          <div className="text-white/50 text-xs whitespace-nowrap">
            <span className="text-teal-300 font-bold">{draftedCount}</span>/6 drafted
          </div>
          <button
            onClick={draftAll}
            disabled={draftingAll || !domainId}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm shrink-0"
            data-testid="archetype-draft-all"
          >
            {draftingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {draftingAll ? 'Drafting all 6… (~1-2 min)' : 'Draft all 6 with AI'}
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-200" data-testid="archetype-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {draftAllResult && (
          <div className="mb-4 bg-teal-500/5 border border-teal-500/30 rounded-lg p-4" data-testid="archetype-draft-all-result">
            <div className="text-teal-300 font-semibold text-sm mb-2">Draft-all complete</div>
            <div className="grid md:grid-cols-3 gap-2 text-xs">
              {Object.entries(draftAllResult).map(([a, res]) => (
                <div key={a} className={`flex items-center gap-2 px-2.5 py-1.5 rounded ${res.ok ? 'bg-teal-500/10 text-teal-200' : 'bg-red-500/10 text-red-200'}`}>
                  {res.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                  <span className="font-mono">{a}</span>
                  <span className="ml-auto">{res.ok ? `v${res.version}` : (res.error || '').slice(0, 40)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && !playbooks.length ? (
          <div className="text-center py-12 text-white/50">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            Loading playbooks…
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {playbooks.map((p) => (
              <PlaybookCard
                key={p.archetype}
                playbook={p}
                onDraft={draftOne}
                drafting={draftingArchetype === p.archetype}
                headers={headers}
                domainId={domainId}
                onRefreshList={load}
              />
            ))}
          </div>
        )}

        <div className="mt-8 bg-teal-500/5 border border-teal-500/20 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-teal-400 shrink-0 mt-0.5" />
            <div className="text-sm text-white/80 leading-relaxed">
              <p className="text-white font-semibold mb-1">How these playbooks work</p>
              <ul className="list-disc pl-5 space-y-1 text-white/70 text-xs">
                <li>One playbook per (domain, archetype). Six archetypes align with the TFVF V1.2 grader&apos;s archetype set.</li>
                <li>Redrafting archives the prior version to <code className="bg-white/10 px-1.5 rounded text-[10px]">archetype_playbooks_history</code> — nothing is destroyed.</li>
                <li>Drafts use Green-Brain coaching model + DISC tone adaptation by default. Total Fusion also gets their approved-phrase library injected.</li>
                <li>The checklist in each playbook maps to the exact in-scope grading behaviours for that archetype — so following the playbook naturally targets a top-band score.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminArchetypePlaybooksPage;
